import React, { FormEventHandler, useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useControls } from 'src/components/async/model/control/useControls'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { FrameworkSectionsEditor } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor'
import { createEmptySectionDraft } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.helpers'
import {
  FrameworkSectionsEditorErrors,
  SectionDraft,
} from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'
import { useFrameworkSections } from 'src/components/async/Framework/useFrameworkSections'
import {
  DuplicateSectionNameError,
  useAddFrameworkSections,
} from 'src/pages/Frameworks/frameworks.hooks'
import { ModalForm, ModalFormId } from '../..'

type Props = {
  framework: ComplianceFramework
}

export const AddFrameworkSectionsManualModal = ({
  framework,
}: Props): JSX.Element => {
  const cfModelId = framework.getModelId()
  const show = useIsShowModal(ModalFormId.ADD_FRAMEWORK_SECTIONS_MANUAL)
  const hide = useHideModal({
    modalId: ModalFormId.ADD_FRAMEWORK_SECTIONS_MANUAL,
  })
  const addFrameworkSections = useAddFrameworkSections()

  const { data: sectionsData, isLoading: sectionsLoading } =
    useFrameworkSections(cfModelId, show)
  const { data: controlsData, isLoading: controlsLoading } = useControls(
    new ListControlsRequest(),
    show,
  )

  const controlOptions = useMemo<SelectItem[]>(
    () =>
      (controlsData?.getItemsList() ?? []).map((c) => ({
        name: `${c.getModelId()} ${c.getName()}`,
        value: c.getModelId(),
      })),
    [controlsData],
  )

  const [sections, setSections] = useState<SectionDraft[]>([
    createEmptySectionDraft(),
  ])
  const setSectionsEnsuringAtLeastOne: React.Dispatch<
    React.SetStateAction<SectionDraft[]>
  > = useCallback((updater) => {
    setSections((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      return next.length > 0 ? next : [createEmptySectionDraft()]
    })
  }, [])

  const [sectionNameErrors, setSectionNameErrors] = useState<
    Record<string, string>
  >({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const closeAndReset = () => {
    setSections([createEmptySectionDraft()])
    setSectionNameErrors({})
    setIsSubmitting(false)
    hide()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    setSectionNameErrors({})
    setIsSubmitting(true)
    try {
      await addFrameworkSections({
        complianceFrameworkModelId: cfModelId,
        existingSections: sectionsData?.getSectionsList() ?? [],
        sections,
      })
      toast.success('Sections added.')
      closeAndReset()
    } catch (err) {
      if (err instanceof DuplicateSectionNameError) {
        const duplicatesByNormalizedName = new Set(
          err.duplicateSectionNames.map((name) => name.trim().toLowerCase()),
        )
        const nextErrors: Record<string, string> = {}
        sections.forEach((section) => {
          if (
            duplicatesByNormalizedName.has(section.name.trim().toLowerCase())
          ) {
            nextErrors[section.tempId] = 'Section name already exists.'
          }
        })
        setSectionNameErrors(nextErrors)
      }
      const errorMessage = getErrorMessage(err as RpcError)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('AddFrameworkSectionsManualModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const errors: FrameworkSectionsEditorErrors = useMemo(
    () => ({
      sectionNames: sectionNameErrors,
    }),
    [sectionNameErrors],
  )

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.ADD_FRAMEWORK_SECTIONS_MANUAL}
      size="xl"
      title="Add Sections"
      submitText="Add sections"
      showOverflow
      disableSubmitButton={isSubmitting || controlsLoading || sectionsLoading}
    >
      <form id={ModalFormId.ADD_FRAMEWORK_SECTIONS_MANUAL} onSubmit={onSubmit}>
        <FrameworkSectionsEditor
          sections={sections}
          setSections={setSectionsEnsuringAtLeastOne}
          controlOptions={controlOptions}
          errors={errors}
        />
      </form>
    </ModalForm>
  )
}
